/**
 * Generic error handling utilities for CRUD operations
 */

import { ApolloError } from '@apollo/client/core'
import { ref } from 'vue'

// Global alert state for custom alerts
const alertState = ref({
  show: false,
  type: 'info' as 'success' | 'error' | 'warning' | 'info',
  title: '',
  message: '',
  confirmText: 'OK',
  cancelText: 'Cancel',
  showCancel: false
})

// Export alert state for components to use
export { alertState }

export interface ErrorInfo {
  message: string
  isUserFriendly: boolean
  shouldRetry: boolean
}

/**
 * Parse Apollo/GraphQL errors into user-friendly messages
 */
export function parseGraphQLError(error: ApolloError | Error | unknown): ErrorInfo {
  if (error instanceof ApolloError) {
    // Handle GraphQL errors
    if (error.graphQLErrors?.length > 0) {
      const gqlError = error.graphQLErrors[0]
      return {
        message: gqlError.message,
        isUserFriendly: true,
        shouldRetry: false
      }
    }

    // Handle network errors
    if (error.networkError) {
      if ('statusCode' in error.networkError) {
        const statusCode = error.networkError.statusCode
        if (statusCode === 401 || statusCode === 403) {
          return {
            message: 'Authentication error. Please log in again.',
            isUserFriendly: true,
            shouldRetry: false
          }
        }
        if (statusCode >= 500) {
          return {
            message: 'Server error. Please try again later.',
            isUserFriendly: true,
            shouldRetry: true
          }
        }
      }
      return {
        message: 'Network error. Please check your connection.',
        isUserFriendly: true,
        shouldRetry: true
      }
    }
  }

  // Handle generic errors
  if (error instanceof Error) {
    return {
      message: error.message || 'An unexpected error occurred.',
      isUserFriendly: false,
      shouldRetry: false
    }
  }

  // Unknown error
  return {
    message: 'An unexpected error occurred. Please try again.',
    isUserFriendly: true,
    shouldRetry: false
  }
}

/**
 * CRUD operation error messages
 */
export const CRUD_MESSAGES = {
  CREATE: {
    SUCCESS: 'Item created successfully!',
    ERROR: 'Failed to create item. Please try again.',
  },
  UPDATE: {
    SUCCESS: 'Item updated successfully!',
    ERROR: 'Failed to update item. Please try again.',
  },
  DELETE: {
    SUCCESS: 'Item deleted successfully!',
    ERROR: 'Failed to delete item. Please try again.',
    CONFIRM: 'Are you sure you want to delete this item? This action cannot be undone.',
  },
  FETCH: {
    ERROR: 'Failed to load data. Please try again.',
  }
}

/**
 * Show custom alert
 */
function showCustomAlert(
  type: 'success' | 'error' | 'warning' | 'info',
  title: string,
  message: string,
  options: {
    confirmText?: string
    cancelText?: string
    showCancel?: boolean
  } = {}
) {
  alertState.value = {
    show: true,
    type,
    title,
    message,
    confirmText: options.confirmText || 'OK',
    cancelText: options.cancelText || 'Cancel',
    showCancel: options.showCancel || false
  }
}

/**
 * Show user-friendly error message using custom alert
 */
export function showError(error: ApolloError | Error | unknown, operation = 'operation') {
  const errorInfo = parseGraphQLError(error)
  const message = errorInfo.isUserFriendly 
    ? errorInfo.message 
    : `Failed to perform ${operation}. Please try again.`
  
  showCustomAlert('error', 'Error', message)
  console.error(`${operation} error:`, error)
}

/**
 * Show success message using custom alert
 */
export function showSuccess(message: string, title = 'Success') {
  showCustomAlert('success', title, message)
}

/**
 * Show validation error message using custom alert
 */
export function showValidationError(message: string, title = 'Validation Error') {
  showCustomAlert('warning', title, message)
}

/**
 * Show info message using custom alert
 */
export function showInfo(message: string, title = 'Information') {
  showCustomAlert('info', title, message)
}

/**
 * Close the current alert
 */
export function closeAlert() {
  alertState.value.show = false
}
